import { css } from '@emotion/react';

import {
  DEFAULT_NEEDS_ATTENTION_INDICATOR_STYLES,
  type NeedsAttentionIndicatorStyles,
} from '@/Client/Widgets/NeedsAttentionIndicator';
import { KEBAB_GUTTER_WIDTH } from '@/Client/Widgets/RowKebabMenu';

import { LAYOUT2 } from '@/Client/Styles';
import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, styleguideLetterSpacing as TRACKING, withOpacity } from '@kaiizen/kai-shiki/core';

const TEXT_SECONDARY = COLORS.midtone;
const TEXT_MUTED = COLORS.midtoneFaded;

export const FOCUSED_TASK_CARD = css`
  label: FOCUSED_TASK_CARD;
  background: ${COLORS.white};
  padding: 18px 22px 22px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-bottom: 1px solid ${COLORS.stroke};
`;

/** Prototype `.sec-taskhdr` — the eyebrow line on its own row, the title row beneath it. */
export const FOCUSED_TASK_CARD_HEADER = css`
  label: FOCUSED_TASK_CARD_HEADER;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/**
 * Prototype `.sec-taskhdr-title-row` — everything the eyebrow sits above shares this one row:
 * the flag/name/status group on the left, the undo/resolve chips and the amount on the right.
 * The title group's own `flex: 1` is what pushes the chips and amount to the row's end.
 */
export const FOCUSED_TASK_CARD_TITLE_ROW = css`
  label: FOCUSED_TASK_CARD_TITLE_ROW;
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex-wrap: nowrap;
`;

/**
 * Prototype `.sec-taskhdr-eyerow` — the work-status badge and the eyebrow share this one
 * line, badge first (prototype: "pill and eyebrow on one line, the pill first").
 */
export const FOCUSED_TASK_CARD_EYEROW = css`
  label: FOCUSED_TASK_CARD_EYEROW;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const FOCUSED_TASK_CARD_EYEBROW = css`
  label: FOCUSED_TASK_CARD_EYEBROW;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: ${COLORS.textTertiary};
`;

export const FOCUSED_TASK_CARD_EYEBROW_SEP = css`
  margin: 0 12px;
  color: ${COLORS.midtone};
`;

/**
 * `flex-wrap: nowrap` is deliberate — with `wrap`, a long task name has nowhere to shrink
 * to (flex items default to `min-width: auto`), so the whole name+pill drop to a second
 * line under the flag icon instead of the name itself wrapping. `nowrap` keeps the flag
 * pinned on the same line; FOCUSED_TASK_CARD_TITLE_NAME below is what actually wraps.
 */
export const FOCUSED_TASK_CARD_TITLE = css`
  label: FOCUSED_TASK_CARD_TITLE;
  display: flex;
  align-items: baseline;
  gap: 20px;
  min-width: 0;
  flex: 1;
  flex-wrap: nowrap;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.displayModalTitle)};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
  letter-spacing: -0.005em;
  line-height: 1.2;
`;

/**
 * The task name itself — the one part of FOCUSED_TASK_CARD_TITLE that's allowed to wrap.
 * `min-width: 0` overrides the flex item's default `min-width: auto`, which is what let a
 * long name overflow instead of shrinking; `overflow-wrap: anywhere` (prototype `.sec-taskhdr
 * .sec-taskcard-title`) breaks a single unbroken long word rather than overflowing past it.
 */
export const FOCUSED_TASK_CARD_TITLE_NAME = css`
  label: FOCUSED_TASK_CARD_TITLE_NAME;
  min-width: 0;
  flex: 1 1 auto;
  overflow-wrap: anywhere;
`;

export const FOCUSED_TASK_CARD_HEADER_CHIPS = css`
  label: FOCUSED_TASK_CARD_HEADER_CHIPS;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

export const FOCUSED_TASK_CARD_UNDO_BUTTON = css`
  label: FOCUSED_TASK_CARD_UNDO_BUTTON;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  color: ${TEXT_SECONDARY};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s, background 0.12s;
  line-height: 1;

  svg {
    width: 11px;
    height: 11px;
    stroke: currentColor;
    fill: none;
    flex-shrink: 0;
  }

  &:hover {
    color: ${COLORS.ink};
    border-color: ${COLORS.ink};
    background: ${COLORS.sunken};
  }
`;

export const FOCUSED_TASK_CARD_DELETE_DISCREET = css`
  label: FOCUSED_TASK_CARD_DELETE_DISCREET;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  background: transparent;
  border: 0;
  color: ${TEXT_MUTED};
  cursor: pointer;
  transition: color 0.12s, background 0.12s;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover {
    color: ${COLORS.error};
    background: rgba(192, 68, 58, 0.08);
  }
`;

export const FOCUSED_TASK_CARD_DELETE_REVIEW = css`
  label: FOCUSED_TASK_CARD_DELETE_REVIEW;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  min-width: 36px;
  padding: 8px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  color: ${COLORS.error};
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s, background 0.12s;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover {
    background: ${COLORS.error};
    border-color: ${COLORS.error};
    color: ${COLORS.white};
  }
`;

export const FOCUSED_TASK_CARD_AMOUNT = css`
  label: FOCUSED_TASK_CARD_AMOUNT;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.displaySmall)};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
`;

export const FOCUSED_TASK_CARD_AMOUNT_LINE = css`
  label: FOCUSED_TASK_CARD_AMOUNT_LINE;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  white-space: nowrap;
  justify-content: flex-end;
`;

export const FOCUSED_TASK_CARD_SECTION = css`
  label: FOCUSED_TASK_CARD_SECTION;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const FOCUSED_TASK_CARD_LABEL = css`
  label: FOCUSED_TASK_CARD_LABEL;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.textTertiary};
`;

export const FOCUSED_TASK_CARD_DESCRIPTION = css`
  label: FOCUSED_TASK_CARD_DESCRIPTION;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  color: ${COLORS.ink};
  line-height: 1.5;
  white-space: pre-wrap;
`;

export const FOCUSED_TASK_CARD_DESCRIPTION_EMPTY = css`
  label: FOCUSED_TASK_CARD_DESCRIPTION_EMPTY;
  color: ${TEXT_MUTED};
  font-style: italic;
`;

export const FOCUSED_TASK_FLAG_STYLES: NeedsAttentionIndicatorStyles = {
  root: css`
    ${DEFAULT_NEEDS_ATTENTION_INDICATOR_STYLES.root};
    margin-right: -10px;
  `,
  active: DEFAULT_NEEDS_ATTENTION_INDICATOR_STYLES.active,
};

/**
 * The "Task Details" accordion's header — contractor and description share this one
 * fold (see FOCUSED_TASK_DETAILS_BODY below). Reads like a module title ("Notes",
 * "Options"), so the caret sits with the label rather than out at the row's edge, and
 * the button stays full-width so the whole strip is still the click target.
 */
export const FOCUSED_TASK_DETAILS_HEADER_STYLE = css`
  label: FOCUSED_TASK_DETAILS_HEADER_STYLE;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  gap: 7px;
  padding: 0;
`;

/**
 * Contractor and description side by side: the contractor is a fixed-width control
 * and the description is prose, so stacking them cost the control's full height for
 * nothing. Narrow panels stack instead of squeezing the prose into a ribbon.
 */
export const FOCUSED_TASK_DETAILS_BODY_STYLE = css`
  label: FOCUSED_TASK_DETAILS_BODY_STYLE;
  display: grid;
  grid-template-columns: 236px minmax(0, 1fr);
  gap: 12px 24px;
  align-items: start;
  padding-top: 2px;

  & > * {
    min-width: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const FOCUSED_TASK_CARD_OPTIONS = css`
  label: FOCUSED_TASK_CARD_OPTIONS;
  display: flex;
  flex-direction: column;
`;

/** A task's values, two to a row; a field that needs the room spans the row (FOCUSED_TASK_CARD_VALUE_FULL_WIDTH). */
export const FOCUSED_TASK_CARD_VALUES = css`
  label: FOCUSED_TASK_CARD_VALUES;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${LAYOUT2.spacing1}px;
`;

export const FOCUSED_TASK_CARD_VALUE_FULL_WIDTH = css`
  label: FOCUSED_TASK_CARD_VALUE_FULL_WIDTH;
  grid-column: 1 / -1;
`;

/** Options carry no card chrome; a single hairline separates each option from the next. */
export const FOCUSED_OPT_CARD = css`
  label: FOCUSED_OPT_CARD;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  padding: 14px 0;
  border-top: 1px solid ${COLORS.stroke};

  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }

  &:last-of-type {
    padding-bottom: 0;
  }
`;

export const FOCUSED_OPT_CARD_TOP = css`
  label: FOCUSED_OPT_CARD_TOP;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

export const FOCUSED_OPT_CARD_MENU = css`
  label: FOCUSED_OPT_CARD_MENU;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-top: -2px;
`;

export const FOCUSED_OPT_CARD_NAME = css`
  label: FOCUSED_OPT_CARD_NAME;
  flex: 1;
  min-width: 0;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
  letter-spacing: -0.005em;
`;

export const FOCUSED_OPT_CARD_DESCRIPTION = css`
  label: FOCUSED_OPT_CARD_DESCRIPTION;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.regular};
  color: ${TEXT_SECONDARY};
  line-height: 1.35;
`;

export const FOCUSED_OPT_CARD_TOTAL = css`
  label: FOCUSED_OPT_CARD_TOTAL;
  flex-shrink: 0;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.bodyLarge)};
  font-weight: ${FONT_WEIGHT.bold};
  color: ${COLORS.ink};
  line-height: 1.35;
  white-space: nowrap;
`;

export const FOCUSED_OPT_CARD_LINE_ITEMS = css`
  label: FOCUSED_OPT_CARD_LINE_ITEMS;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
`;

/** Marks a candidate row's Add button, which the row reveals on hover. */
export const LINE_ITEM_ADD_BUTTON_ATTRIBUTE = 'data-line-item-add-button';
/** Marks a candidate row's $0.00 total, which gives its slot to the Add button on hover. */
export const LINE_ITEM_ZERO_TOTAL_ATTRIBUTE = 'data-line-item-zero-total';

/**
 * Three major columns: product/description info on the left, the value fields in the center
 * (each taking half the flexible space), and the total pinned on the far right. The base row is
 * the candidate (not-added) presentation; in-scope rows layer FOCUSED_LINE_ITEM_ROW_ADDED.
 */
export const FOCUSED_LINE_ITEM_ROW = css`
  label: FOCUSED_LINE_ITEM_ROW;
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 78px;
  padding: ${LAYOUT2.spacing1p5}px 12px;
  background: ${COLORS.candidateTint};
  transition: background 0.1s;
`;

/** A line item that's in the scope sits on the heavier ground. */
export const FOCUSED_LINE_ITEM_ROW_ADDED = css`
  label: FOCUSED_LINE_ITEM_ROW_ADDED;
  background: ${COLORS.scopedTint};
`;

/**
 * A candidate row the user can add: on approach (hover on desktop, tap on iPad) it takes the
 * accent wash and Add appears in the slot its total will occupy.
 */
export const FOCUSED_LINE_ITEM_ROW_ADDABLE = css`
  label: FOCUSED_LINE_ITEM_ROW_ADDABLE;

  &:hover {
    background: ${withOpacity(COLORS.accent, 0.5)};
  }

  &:hover [${LINE_ITEM_ADD_BUTTON_ATTRIBUTE}] {
    opacity: 1;
  }

  &:hover [${LINE_ITEM_ZERO_TOTAL_ATTRIBUTE}] {
    opacity: 0;
  }
`;

/**
 * A line item with something to flag (a catalog price change) frames the row and the strip that
 * explains it as one unit, in an ink hairline.
 */
export const FOCUSED_LINE_ITEM_FLAGGED = css`
  label: FOCUSED_LINE_ITEM_FLAGGED;
  border: 1px solid ${COLORS.ink};
`;

/** Layered onto FOCUSED_LINE_ITEM_FLAGGED for out of stock, an override, or a change order: the frame goes red. */
export const FOCUSED_LINE_ITEM_FLAGGED_ERROR = css`
  label: FOCUSED_LINE_ITEM_FLAGGED_ERROR;
  border-color: ${COLORS.error};
`;

/** A row carrying tabs drops its content below them. */
export const FOCUSED_LINE_ITEM_ROW_WITH_TABS = css`
  label: FOCUSED_LINE_ITEM_ROW_WITH_TABS;
  min-height: 94px;
  padding-top: 32px;
`;

/** A row with default captions under its fields makes room for them below. */
export const FOCUSED_LINE_ITEM_ROW_WITH_CAPTIONS = css`
  label: FOCUSED_LINE_ITEM_ROW_WITH_CAPTIONS;
  padding-bottom: 28px;
`;

/** The row's tabs, flush in its top-left corner. */
export const FOCUSED_LINE_ITEM_TABS = css`
  label: FOCUSED_LINE_ITEM_TABS;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  display: flex;
  gap: ${LAYOUT2.spacingHalf}px;
`;

/** A row tab: the flag's name ("Price change"), in ink. */
export const FOCUSED_LINE_ITEM_TAB = css`
  label: FOCUSED_LINE_ITEM_TAB;
  padding: 4px 9px 3px;
  background: ${COLORS.ink};
  color: ${COLORS.paper};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(8.5)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${TRACKING.monoCaption};
  text-transform: uppercase;
  white-space: nowrap;
  /* the arrow: a tab explains itself on hover, but it's neither text to select nor a control */
  cursor: default;
`;

/** Layered onto FOCUSED_LINE_ITEM_TAB for an override: white, lettered and ruled in red, a tab that flags without blocking. */
export const FOCUSED_LINE_ITEM_TAB_WARN = css`
  label: FOCUSED_LINE_ITEM_TAB_WARN;
  background: ${COLORS.white};
  color: ${COLORS.error};
  border-right: 1px solid ${COLORS.error};
  border-bottom: 1px solid ${COLORS.error};

  &:not(:first-of-type) {
    border-left: 1px solid ${COLORS.error};
  }
`;

/** Layered onto FOCUSED_LINE_ITEM_TAB for the line's instructions: midtone, with a lines glyph. */
export const FOCUSED_LINE_ITEM_TAB_NOTE = css`
  label: FOCUSED_LINE_ITEM_TAB_NOTE;
  display: inline-flex;
  align-items: center;
  gap: ${LAYOUT2.spacingHalf}px;
  background: ${COLORS.midtone};
  color: ${COLORS.paper};

  & svg {
    width: 9px;
    height: 9px;
  }
`;

/** Layered onto FOCUSED_LINE_ITEM_TAB for a modifier ("Resident Responsible"): quiet, a classification rather than a flag. */
export const FOCUSED_LINE_ITEM_TAB_MODIFIER = css`
  label: FOCUSED_LINE_ITEM_TAB_MODIFIER;
  padding: 3px 8px 2px;
  background: ${COLORS.paper};
  color: ${COLORS.midtone};
  border: 1px solid ${COLORS.stroke};
`;

/** Layered onto FOCUSED_LINE_ITEM_TAB for a blocking flag ("Out of stock"). */
export const FOCUSED_LINE_ITEM_TAB_ERROR = css`
  label: FOCUSED_LINE_ITEM_TAB_ERROR;
  background: ${COLORS.error};
  color: ${COLORS.white};
`;

/** Layered onto FOCUSED_LINE_ITEM_VALUE_BOX for the value a flag is about (a parts price the catalog has moved off): framed heavier in ink. */
export const FOCUSED_LINE_ITEM_VALUE_BOX_CHANGED = css`
  label: FOCUSED_LINE_ITEM_VALUE_BOX_CHANGED;
  border: 1.5px solid ${COLORS.ink};
`;

/** The strip under a flagged row: what changed, and the action that resolves it on the right. */
/** Every strip a row needs, stacked full width under it behind one rule. */
export const FOCUSED_LINE_ITEM_STRIPS = css`
  label: FOCUSED_LINE_ITEM_STRIPS;
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${COLORS.ink};
`;

/** Layered onto FOCUSED_LINE_ITEM_STRIPS in a red-framed row: the rule goes red with the frame. */
export const FOCUSED_LINE_ITEM_STRIPS_ERROR = css`
  label: FOCUSED_LINE_ITEM_STRIPS_ERROR;
  border-top-color: ${COLORS.error};
`;

/** A product section that opens its product on a click shows the pointer. */
export const FOCUSED_LINE_ITEM_PRODUCT_OPENS_PRODUCT = css`
  label: FOCUSED_LINE_ITEM_PRODUCT_OPENS_PRODUCT;
  cursor: pointer;
`;

/**
 * Groups the fields and total columns (plus the kebab). The flex basis reserves the fixed
 * total + kebab width so the flexible space still splits roughly half/half between the
 * product column and the fields column.
 */
export const FOCUSED_LINE_ITEM_RIGHT_COLUMNS = css`
  label: FOCUSED_LINE_ITEM_RIGHT_COLUMNS;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  flex: 1 1 151px;
  min-width: 0;
`;

/**
 * A value column: its label sits directly over the box it names, both flush right. The editable
 * inputs it hosts (LabeledInput's taskCardField) are resized here to the row's 30px box, and
 * framed in midtone at rest; focus and an override's red frame still come from the input itself.
 */
export const FOCUSED_LINE_ITEM_FIELD = css`
  label: FOCUSED_LINE_ITEM_FIELD;
  --border2: ${COLORS.midtone};
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 48px;
  min-width: 0;
  flex: 0 0 auto;

  & [data-input-container] {
    min-height: 30px;
    max-height: 30px;
    font-size: ${pxToRem(13)};
  }
`;

/**
 * Layered onto FOCUSED_LINE_ITEM_FIELD for a value the user has overridden: the input framed
 * heavier and lettered in red. Focus still wins while it's being edited.
 */
export const FOCUSED_LINE_ITEM_FIELD_OVERRIDDEN = css`
  label: FOCUSED_LINE_ITEM_FIELD_OVERRIDDEN;

  & [data-input-container]:not(:focus-within) {
    border: 1.5px solid ${COLORS.error};
    color: ${COLORS.error};
  }

  & [data-input-container]:not(:focus-within) input {
    color: ${COLORS.error};
  }
`;

export const FOCUSED_LINE_ITEM_FIELD_LABEL = css`
  label: FOCUSED_LINE_ITEM_FIELD_LABEL;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.14em;
  line-height: 12px;
  text-transform: uppercase;
  text-align: right;
  white-space: nowrap;
  color: ${COLORS.midtone};
`;

/**
 * Read-only value presentation: recessed, a bone-tinted fill inside a midtone hairline, so it
 * reads as inactive beside the white editable inputs. Weight tells you which you can type in.
 */
export const FOCUSED_LINE_ITEM_VALUE_BOX = css`
  label: FOCUSED_LINE_ITEM_VALUE_BOX;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 30px;
  padding: 0 9px;
  background: ${withOpacity(COLORS.stroke, 0.62)};
  border: 1px solid ${withOpacity(COLORS.midtone, 0.42)};
  font-family: ${FONT.body};
  font-size: ${pxToRem(13)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.15;
  color: ${COLORS.ink};
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

/** On a candidate row's lighter ground, the read-only boxes tint lighter too. */
export const FOCUSED_LINE_ITEM_VALUE_BOX_ON_CANDIDATE = css`
  label: FOCUSED_LINE_ITEM_VALUE_BOX_ON_CANDIDATE;
  background: ${withOpacity(COLORS.stroke, 0.38)};
`;

/** A column the line item has no value for: the box holds its place with a midtone dash. */
export const FOCUSED_LINE_ITEM_VALUE_BOX_EMPTY = css`
  label: FOCUSED_LINE_ITEM_VALUE_BOX_EMPTY;
  color: ${COLORS.midtone};
`;

/**
 * Layered onto FOCUSED_LINE_ITEM_VALUE_BOX for a value edited elsewhere (an equation-driven
 * measurement, on the Measurements tab): white and framed like the inputs, since it can be changed.
 */
export const FOCUSED_LINE_ITEM_VALUE_BOX_EDITED_ELSEWHERE = css`
  label: FOCUSED_LINE_ITEM_VALUE_BOX_EDITED_ELSEWHERE;
  background: ${COLORS.white};
  border-color: ${COLORS.midtone};
`;

/** Layered onto FOCUSED_LINE_ITEM_VALUE_BOX for a read-only value that navigates somewhere on click (an equation-driven measurement). */
export const FOCUSED_LINE_ITEM_VALUE_LINK = css`
  label: FOCUSED_LINE_ITEM_VALUE_LINK;
  width: 100%;
  cursor: pointer;
  text-decoration-color: transparent;
  transition: text-decoration-color 0.12s;

  &:hover {
    text-decoration: underline;
  }
`;

export const FOCUSED_LINE_ITEM_PRODUCT = css`
  label: FOCUSED_LINE_ITEM_PRODUCT;
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 10px;
  min-width: 0;
  flex: 1 1 0;
`;

/** The center column: the value fields, right-aligned and wrapping. */
export const FOCUSED_LINE_ITEM_FIELDS = css`
  label: FOCUSED_LINE_ITEM_FIELDS;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  min-width: 0;
  flex: 1 1 0;
`;

/** The photo's place in the product cell, anchoring the default caption an overridden product hangs under it. */
export const FOCUSED_LINE_ITEM_PRODUCT_PHOTO_SLOT = css`
  label: FOCUSED_LINE_ITEM_PRODUCT_PHOTO_SLOT;
  position: relative;
  flex: 0 0 auto;
`;

export const FOCUSED_LINE_ITEM_PRODUCT_META = css`
  label: FOCUSED_LINE_ITEM_PRODUCT_META;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const FOCUSED_LINE_ITEM_PRODUCT_BRAND = css`
  label: FOCUSED_LINE_ITEM_PRODUCT_BRAND;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
`;

export const FOCUSED_LINE_ITEM_PRODUCT_NAME = css`
  label: FOCUSED_LINE_ITEM_PRODUCT_NAME;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: -0.005em;
  color: ${COLORS.ink};
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-width: 0;
`;

export const FOCUSED_LINE_ITEM_PRODUCT_SKU = css`
  label: FOCUSED_LINE_ITEM_PRODUCT_SKU;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  letter-spacing: 0.04em;
  color: ${COLORS.midtone};
`;

/** The Total column's width, less the kebab's space the total slot adds to it. */
export const FOCUSED_LINE_ITEM_TOTAL_WIDTH = 96;

/**
 * The trailing slot: the Total column, then the space the row's kebab sits in, reserved on every
 * row so the totals line up whether or not a row has a kebab (a row not yet added has none).
 */
export const FOCUSED_LINE_ITEM_TOTAL_SLOT = css`
  label: FOCUSED_LINE_ITEM_TOTAL_SLOT;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  flex: 0 0 auto;
  min-width: ${FOCUSED_LINE_ITEM_TOTAL_WIDTH + KEBAB_GUTTER_WIDTH}px;
  padding-right: ${KEBAB_GUTTER_WIDTH}px;
`;

/** The total is a computed figure, not a field: it never gets a box. */
export const FOCUSED_LINE_ITEM_TOTAL = css`
  label: FOCUSED_LINE_ITEM_TOTAL;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 30px;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyLarge)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: -0.008em;
  color: ${COLORS.ink};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  /* the arrow: hovering it explains the calculation, but it's neither text to select nor a control */
  cursor: default;
  transition: opacity 0.1s;
`;

/** A $0.00 total (the line item isn't in the scope) reads muted rather than like a real cost. */
export const FOCUSED_LINE_ITEM_TOTAL_ZERO = css`
  label: FOCUSED_LINE_ITEM_TOTAL_ZERO;
  color: ${COLORS.midtone};
  font-weight: ${FONT_WEIGHT.regular};
`;

/** Add sits in the Total slot: the cell that will hold the total once the line item is in. */
export const FOCUSED_LINE_ITEM_ADD_BUTTON = css`
  label: FOCUSED_LINE_ITEM_ADD_BUTTON;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 30px;
  padding: 0;
  border: 0;
  background: ${COLORS.ink};
  color: ${COLORS.paper};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.1s;

  &:hover {
    background: ${withOpacity(COLORS.ink, 0.9)};
  }

  &:focus-visible {
    opacity: 1;
  }
`;

export const FOCUSED_OPT_CARD_NOTES_BUTTON = css`
  label: FOCUSED_OPT_CARD_NOTES_BUTTON;
  width: 26px;
  height: 26px;
`;
